import { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { LoadingPage } from './components/ui/LoadingSpinner';
import { UserSettingsProvider } from './contexts/UserSettingsContext';
import { ToastProvider } from './contexts/ToastContext';
import { PWAUpdatePrompt, OfflineIndicator } from './components/pwa';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import AmbientDust from './components/ui/AmbientDust';
import { useVocabularyPreload } from './hooks/useVocabulary';
import { useFIReInit } from './hooks/useFIReInit';
import { loadManifest } from './lib/contentStatsCore';
import { runStartupMaintenance } from './lib/storageRetentionService';
import { refreshSubscription } from './lib/subscriptionService';
import { applyAmbientTime } from './lib/ambientTimeService';
import { allRoutes, renderRoute } from './routes';
import { TabLayout } from './components/layout/TabLayout';

/**
 * Preload content in background after initial render.
 * This starts loading vocabulary and manifest data early
 * so they're ready when users navigate to other pages.
 */
function useBackgroundPreload() {
  useVocabularyPreload();

  // Initialize FIRe spaced repetition system
  // This builds the encompassing graph from lesson data
  useFIReInit();

  // Storage retention: prune expired data, handle high quota
  useEffect(() => {
    runStartupMaintenance();
  }, []);

  // Refresh subscription status from backend (silently fails if offline)
  useEffect(() => {
    refreshSubscription().catch(() => {});
  }, []);

  // Apply time-of-day ambient color class to <html>
  useEffect(() => applyAmbientTime(), []);

  // Also preload the content manifest for stats
  // Uses same delayed pattern as vocabulary
  if (typeof window !== 'undefined') {
    setTimeout(() => {
      loadManifest().catch(() => {
        // Errors will be handled by components
      });
    }, 150);
  }
}

function App() {
  // Start background preloading of vocabulary and manifest
  useBackgroundPreload();

  return (
    <BrowserRouter>
      <AmbientDust />
      <ToastProvider>
      <UserSettingsProvider>
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-[var(--color-primary)] focus:text-white focus:px-4 focus:py-2 focus:rounded-lg"
        >
          Skip to main content
        </a>
        <OfflineIndicator />
        <ErrorBoundary>
          <Routes>
            {/* Tab routes share a layout with persistent BottomNav */}
            <Route element={<TabLayout />}>
              {allRoutes.filter(r => r.layout === 'tabs').map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={renderRoute(route)}
                />
              ))}
            </Route>
            {/* Other routes get their own Suspense boundary */}
            <Route element={<Suspense fallback={<LoadingPage message="Loading page..." />}><Outlet /></Suspense>}>
              {allRoutes.filter(r => r.layout !== 'tabs').map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={renderRoute(route)}
                />
              ))}
            </Route>
          </Routes>
        </ErrorBoundary>
        <PWAUpdatePrompt />
      </UserSettingsProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
